import { Route } from "@src/routes/routes";
import ThingsTwinsIcon from "@src/assets/svg/things-twins.svg";
import ThingsSupportIcon from "@src/assets/svg/things-support.svg";
import ThingsGatewayIcon from "@src/assets/svg/things-gateways.svg";
import ThingsDashboardIcon from "@src/assets/svg/things-dashboard.svg";
import navigateTo from "@src/routes/navigator";
import { HomepageCard } from "@src/components/molecules/homepage-card/homepage-card";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";

export interface UseCreateAppsCardsResult {
  cards: HomepageCard[];
  permissionsLoaded: boolean;
}

export const useCreateAppsCards = (): UseCreateAppsCardsResult => {
  /*   const i18n = useI18N(); */
  const permissions = useSelector(
    (state: RootState) => state.yourSlice.authContext?.permissions,
  );

  const dispatch = useDispatch();

  // `undefined` means the auth context hasn't hydrated yet.
  // `[]` means it has hydrated but the user has no permissions.
  const permissionsLoaded = permissions !== undefined;
  const permissionSet = new Set(permissions ?? []);

  const hasAnyPermission = (required: string[]) =>
    required.some((p) => permissionSet.has(p));

  const handleThingsSupport = () => {
    const canCreateTickets =
      !permissionSet.has("manage-all-tickets") &&
      permissionSet.has("create-tickets");
    if (canCreateTickets) {
      dispatch(
        updateModalsCloseDrawerModal({
          showModal: true,
          id: "addRequestTechnicalSupportDrawerModalWithoutMachine",
        }),
      );
      return;
    }
    navigateTo(Route.TICKETS_PAGE);
  };

  const handleThingsTwins = () => {
    navigateTo(Route.MACHINES);
  };

  const handleThingsGateway = () => {
    navigateTo(Route.GATEWAYS);
  };

  /* const handleUserManagement = () => {
    navigateTo(Route.USER_MANAGEMENT_PAGE);
  }; */

  const handleDashboard = () => {
    navigateTo(Route.DASHBOARD);
  };

  const cards: (HomepageCard & { visible: boolean })[] = [
    {
      id: "things-support",
      color: { r: 21, g: 38, b: 43 },
      image: ThingsSupportIcon,
      title: "Support",
      description: "",
      visible: hasAnyPermission([
        "manage-all-tickets",
        "view-all-tickets",
        "create-tickets",
      ]),
      onClick: handleThingsSupport,
    },
    {
      id: "things-twins",
      color: { r: 21, g: 38, b: 43 },
      image: ThingsTwinsIcon,
      title: "Twins",
      description: "",
      visible: true,
      onClick: handleThingsTwins,
    },
    {
      id: "things-gateway",
      color: { r: 21, g: 38, b: 43 },
      image: ThingsGatewayIcon,
      title: "Gateways",
      description: "",
      visible: permissionSet.has("manage-gateways"),
      onClick: handleThingsGateway,
    },
    /* {
      id: "user-management",
      color: { r: 21, g: 38, b: 43 },
      image: UserManagementIcon,
      title: "Users",
      description: "",
      visible: permissionSet.has("view-human-users"),
      onClick: handleUserManagement,
    }, */
    {
      id: "dashboard",
      color: { r: 21, g: 38, b: 43 },
      image: ThingsDashboardIcon,
      title: "Dashboard",
      description: "",
      visible: permissionSet.has("view-dashboards"),
      onClick: handleDashboard,
    },
  ];

  return {
    // Return empty array while permissions are not yet loaded to avoid
    // a flash of permission-free cards before auth context hydrates.
    cards: permissionsLoaded ? cards.filter((card) => card.visible) : [],
    permissionsLoaded,
  };
};
